import { useEffect, useRef, useState } from "react";
import type { ComponentProps } from "react";

const RECIPIENT = "hello@backbonesystems.ai";

/**
 * WHERE SUBMISSIONS GO — the one thing to change when a real destination exists.
 *
 * The site is static and has no backend, so this composes a structured Gmail
 * draft addressed to the team instead of POSTing anywhere. Nothing is stored,
 * no third party receives the data, and no privacy decision is required — but
 * the visitor does see (and has to send) the draft themselves.
 *
 * To make it a true submission, replace `deliver` with a `fetch` POST to an
 * endpoint. See the notes handed over with this change for the options.
 */
const COMPOSE_BASE = `https://mail.google.com/mail/?view=cm&fs=1&to=${RECIPIENT}`;

const deliver = (subject: string, body: string): void => {
  const url = `${COMPOSE_BASE}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  window.open(url, "_blank", "noreferrer");
};

const TEXT_FIELDS = [
  { name: "firstName", label: "First name", type: "text", autoComplete: "given-name" },
  { name: "lastName", label: "Last name", type: "text", autoComplete: "family-name" },
  { name: "jobTitle", label: "Job title", type: "text", autoComplete: "organization-title" },
  { name: "workEmail", label: "Work email", type: "email", autoComplete: "email" },
  { name: "companyName", label: "Company name", type: "text", autoComplete: "organization" },
] as const;

/** Derived from the JSX props so no deprecated React event alias is needed. */
type FormSubmitHandler = NonNullable<ComponentProps<"form">["onSubmit"]>;
type ButtonPointerHandler = NonNullable<ComponentProps<"button">["onPointerMove"]>;

/**
 * How far the ramp has opened. The button reads the pointer's position across
 * its own width, so entering from the left opens the curtain partway and sliding
 * right carries it through to the orange — the direction of travel is visible
 * rather than every hover looking identical.
 */
type SweepZone = "rest" | "left" | "mid" | "right";

/** Touch has no hover, so a tap flashes the full ramp and releases. */
const TAP_FLASH_MS = 520;

const zoneFromPointer = (button: HTMLButtonElement, clientX: number): SweepZone => {
  const { left, width } = button.getBoundingClientRect();
  if (width === 0) return "mid";

  const fraction = (clientX - left) / width;
  if (fraction < 0.34) return "left";
  if (fraction > 0.66) return "right";
  return "mid";
};

export function ContactDialog() {
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const [sweep, setSweep] = useState<SweepZone>("rest");
  const tapTimer = useRef<number | undefined>(undefined);

  useEffect(
    () => () => {
      window.clearTimeout(tapTimer.current);
    },
    [],
  );

  const trackPointer: ButtonPointerHandler = (event) => {
    /* Touch reports a position too, but its state is driven by the tap below. */
    if (event.pointerType === "touch") return;
    setSweep(zoneFromPointer(event.currentTarget, event.clientX));
  };

  const flashOnTap: ButtonPointerHandler = (event) => {
    if (event.pointerType !== "touch") return;
    setSweep("right");
    window.clearTimeout(tapTimer.current);
    tapTimer.current = window.setTimeout(() => {
      setSweep("rest");
    }, TAP_FLASH_MS);
  };

  /*
   * Backdrop dismissal is bound imperatively rather than as a JSX onClick: a
   * <dialog> is a non-interactive element, so an inline handler trips the a11y
   * rules that (correctly) ask for a keyboard equivalent. Escape already closes
   * it, which is the keyboard path, and this only adds the pointer one.
   */
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    const dismissOnBackdrop = (event: MouseEvent): void => {
      if (event.target === dialog) dialog.close();
    };

    dialog.addEventListener("click", dismissOnBackdrop);
    return () => {
      dialog.removeEventListener("click", dismissOnBackdrop);
    };
  }, []);

  const handleSubmit: FormSubmitHandler = (event): void => {
    event.preventDefault();

    const form = event.currentTarget;
    const data = new FormData(form);
    const read = (name: string): string => {
      const raw = data.get(name);
      return typeof raw === "string" ? raw.trim() : "";
    };

    const comments = read("comments");
    const body = [
      ...TEXT_FIELDS.map((field) => `${field.label}: ${read(field.name)}`),
      "",
      `Comments: ${comments === "" ? "(none)" : comments}`,
    ].join("\n");

    deliver(`Demo request — ${read("companyName")}`, body);

    form.reset();
    dialogRef.current?.close();
  };

  return (
    <>
      <button
        className="site-nav__trigger"
        type="button"
        onClick={() => {
          dialogRef.current?.showModal();
        }}
      >
        Contact
      </button>

      <dialog className="contact-dialog" ref={dialogRef} aria-labelledby="contact-dialog-title">
        <form className="contact-form" onSubmit={handleSubmit}>
          <button
            className="contact-form__close"
            type="button"
            aria-label="Close"
            onClick={() => {
              dialogRef.current?.close();
            }}
          >
            <svg aria-hidden="true" viewBox="0 0 24 24">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>

          <h2 id="contact-dialog-title">Bring Backbone to your team</h2>
          <p className="contact-form__lede">Let us support you.</p>

          <div className="contact-form__grid">
            {TEXT_FIELDS.map((field) => (
              <label key={field.name}>
                <span>
                  {field.label}
                  <abbr title="Required">*</abbr>
                </span>
                <input
                  autoComplete={field.autoComplete}
                  name={field.name}
                  required
                  type={field.type}
                />
              </label>
            ))}

            <label className="contact-form__wide">
              <span>Comments</span>
              <textarea name="comments" rows={3} />
            </label>
          </div>

          <button
            className="contact-form__submit"
            type="submit"
            data-sweep={sweep}
            onPointerEnter={trackPointer}
            onPointerMove={trackPointer}
            onPointerDown={flashOnTap}
            onPointerLeave={() => {
              setSweep("rest");
            }}
            onFocus={() => {
              setSweep("mid");
            }}
            onBlur={() => {
              setSweep("rest");
            }}
          >
            Request a demo
          </button>
        </form>
      </dialog>
    </>
  );
}
